/* ===== game/isle-lift.ts — 섬을 든 것이 깨어나면 바다의 외딴 섬(황금 상자 섬)을 그대로 등에 지고 일어선다 ===== */
import { mixin } from '../../engine/core/mixin.js';
import { T, TILE_DEF } from '../data.js';
import { TS } from '../world.js';
import { BODY_ONLY, CONN, TileArt } from '../tileart.js';
import { Game } from '../game.js';
/* ★ 등의 섬은 세계의 그 섬이다(점검판 2026-11-03 "섬이 나타나는 곳은 하늘섬이 아니라 상자 있는 외딴 섬 — 그 자리에서 연속적으로 이동"):
   섬 윗부분(수면 아래 SINK 칸까지 · 야자수째)을 세계에서 들어내 그림으로 굽고, 남은 밑동과 그 옆으로 넓힌 모래톱에 거인이 선다(정수리가 섬 밑으로 드러나게).
   바꾼 칸은 전부 `isleLiftCells` 에 [x, y, 원래 타일, 원래 벽지] 로 적어 두고 싸움이 끝나면(쓰러짐 · 플레이어 죽음) 되돌린다.
   ★ 저장은 들어내기 전 모습으로 — `withoutSeal` 이 이 목록도 잠깐 되돌린다(안 그러면 싸우다 저장하면 섬이 영영 사라진다). 사연: docs/code-history.md#h260 */

export const ISLE_SINK = 3, ISLE_SHOAL = 22;          // 들어내는 깊이(수면 아래 칸 — 남은 밑동이 거인이 딛는 바닥) · 모래톱 반폭(칸)

export const IsleLiftPart: Bag = {
  /** 섬 자리 — 생성 직후엔 world.isle, 불러온 세계엔 없으므로 황금 상자(boss: isle_keeper)에서 되짚는다(sea.ts 와 같은 모양: 반폭 14) */
  isleInfo() {
    const w = this.world, chest = w.objects.find((o: any) => o.type === 'chest' && o.boss === 'isle_keeper');
    if (w.isle) return { ...w.isle, chest };
    if (!chest) return null;
    return { x: Math.floor(chest.x / TS), y: Math.round((chest.y - 6) / TS) + 1, w: 29, chest };
  },
  /** 깨어날 때(호스트) — 섬을 들어내 굽고, 모래톱을 깔고, 거인을 그 밑에 세운다 */
  isleLift(e: any) {
    const w = this.world, I = this.isleInfo(); if (!I || this.isleLiftCells) return;
    const { WW } = w.dims, sea = (w.sea && w.sea.level) ?? I.y, half = (I.w - 1) >> 1;
    const cells: number[][] = [];
    const put = (x: number, y: number, t: number) => {
      if (!w.inB(x, y)) return;
      const k = y * WW + x; cells.push([x, y, w.tiles[k], w.walls[k]]);
      w.set(x, y, t); w.walls[k] = 0;
    };
    let x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
    const grid: number[][] = [];
    for (let y = I.y - 14; y < I.y + ISLE_SINK; y++) for (let x = I.x - half - 4; x <= I.x + half + 4; x++) {
      if (!w.inB(x, y)) continue;
      const t = w.tiles[y * WW + x];
      if (t === T.AIR || t === T.SEAWATER || TILE_DEF[t].liquid) continue;
      grid.push([x, y, t, w.walls[y * WW + x]]);
      x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
    }
    /* 들어내기 **전에** 굽는다 — 야자 줄기 · 잎은 이웃을 보고 그리는 타일(drawConn)이라 세계에 있을 때 그려야 이어진다 */
    let cv: HTMLCanvasElement | null = null;
    if (grid.length) {
      cv = document.createElement('canvas'); cv.width = (x1 - x0 + 1) * TS; cv.height = (y1 - y0 + 1) * TS;
      const c = cv.getContext('2d')!; c.imageSmoothingEnabled = false;
      for (const [x, y, , wl] of grid) if (wl && y < sea) TileArt.drawWall(c, wl, 0, (x - x0) * TS, (y - y0) * TS);
      for (const [x, y, t] of grid) {
        const sx = (x - x0) * TS, sy = (y - y0) * TS, v = ((x * 7 + y * 13) % TileArt.V + TileArt.V) % TileArt.V;
        if (!((BODY_ONLY[t] || CONN[t]) && TileArt.drawConn(c, w, t, x, y, sx, sy, v))) TileArt.draw(c, t, v, sx, sy);
      }
    }
    for (const [x, y] of grid) put(x, y, y >= sea ? T.SEAWATER : T.AIR);
    for (let x = I.x - ISLE_SHOAL; x <= I.x + ISLE_SHOAL; x++) for (let y = I.y + ISLE_SINK; y < I.y + ISLE_SINK + 3; y++) {   // 물에 잠긴 모래톱
      if (!w.inB(x, y)) continue;
      const t = w.tiles[y * WW + x];
      if (t === T.AIR || t === T.SEAWATER) put(x, y, y === I.y + ISLE_SINK ? T.SAND : T.SANDSTONE);
    }
    this.isleLiftCells = cells;
    if (!cv) return;
    const chest = I.chest;
    this.isleLiftChest = chest ? { o: chest, x: chest.x, y: chest.y } : null;
    e.ikIsle = { cv, ox: x0 * TS, oy: y0 * TS, top: I.y * TS, cx: (I.x + 0.5) * TS, half: (half - 4.5) * TS, t0: this.time || 0,
      chest, chestOff: chest ? [chest.x - (I.x + 0.5) * TS, chest.y - I.y * TS] : null, shoal: [(I.x - ISLE_SHOAL + 2) * TS, (I.x + ISLE_SHOAL - 1) * TS] };
    e.x = (I.x + 0.5) * TS - e.w / 2; e.y = (I.y + ISLE_SINK) * TS - e.h; e.vx = e.vy = 0;
  },
  /** 싸움이 끝나면 — 들어낸 섬과 모래톱을 원래대로 */
  isleRestore() {
    const L = this.isleLiftCells; if (!L) return;
    const w = this.world, { WW } = w.dims;
    for (let i = L.length - 1; i >= 0; i--) { const [x, y, t, wl] = L[i]; w.set(x, y, t); w.walls[y * WW + x] = wl; }
    const C = this.isleLiftChest; if (C) { C.o.x = C.x; C.o.y = C.y; }
    for (const e of this.ents) if (e.ikIsle) e.ikIsle = null;
    this.isleLiftCells = null; this.isleLiftChest = null;
  },
};
mixin(Game.prototype, IsleLiftPart, true);
